import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Contact, UserPlus, Building2, Users, Phone, Mail, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPicker = ({ user, categories = [], onContactAdded, defaultAction = "choose" }) => {
  const navigate = useNavigate();
  const [isSupported] = useState(() => 'contacts' in navigator && 'ContactsManager' in window);
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [addAction, setAddAction] = useState(defaultAction); // "listing", "mysubs", or "choose"
  const [isAdding, setIsAdding] = useState(false);
  
  // Manual entry fallback
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualContact, setManualContact] = useState({
    name: "",
    phone: "",
    email: ""
  });

  const pickContact = async () => {
    if (!user) {
      toast.error("Please sign in to add contacts");
      return;
    }

    // Check if Contact Picker API is supported
    if (isSupported) {
      try {
        const props = ['name', 'tel', 'email'];
        const opts = { multiple: false };
        
        const contacts = await navigator.contacts.select(props, opts);
        
        if (contacts && contacts.length > 0) {
          const contact = contacts[0];
          setSelectedContact({
            name: contact.name?.[0] || "",
            phone: contact.tel?.[0] || "",
            email: contact.email?.[0] || ""
          });
          setShowModal(true);
        }
      } catch (error) {
        if (error.name === 'SecurityError') {
          toast.error("Contact access denied. Please allow access in your browser settings.");
        } else if (error.name !== 'InvalidStateError') {
          // User cancelled - don't show error
          console.error("Contact picker error:", error);
          // Fall back to manual entry
          setShowManualEntry(true);
          setShowModal(true);
        }
      }
    } else {
      // Browser doesn't support Contact Picker - show manual entry
      setShowManualEntry(true);
      setShowModal(true);
    }
  };

  const handleAddAsListing = async () => {
    if (!selectedCategory) {
      toast.error("Please select a trade category");
      return;
    }

    const contact = showManualEntry ? manualContact : selectedContact;
    if (!contact?.name) {
      toast.error("Contact name is required");
      return;
    }

    setIsAdding(true);
    try {
      await axios.post(`${API}/services`, {
        name: contact.name,
        category_id: selectedCategory,
        description: `${contact.name} - Contact imported from contacts`,
        phone: contact.phone || "",
        email: contact.email || "",
        address: "",
        city: "New Braunfels",
        website: "",
        hours: "",
        image_url: ""
      }, { withCredentials: true });

      toast.success(`${contact.name} added as a listing!`);
      setShowModal(false);
      resetForm();
      
      if (onContactAdded) {
        onContactAdded("listing");
      }
      
      // Navigate to provider dashboard to see the new listing
      navigate("/provider");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create listing");
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddToMySubs = async () => {
    if (!selectedCategory) {
      toast.error("Please select a trade category");
      return;
    }

    const contact = showManualEntry ? manualContact : selectedContact;
    if (!contact?.name) {
      toast.error("Contact name is required");
      return;
    }

    setIsAdding(true);
    try {
      // First create a basic service entry for this contact
      const serviceRes = await axios.post(`${API}/services`, {
        name: contact.name,
        category_id: selectedCategory,
        description: `${contact.name} - Added from contacts`,
        phone: contact.phone || "",
        email: contact.email || "",
        address: "",
        city: "New Braunfels",
        website: "",
        hours: "",
        image_url: ""
      }, { withCredentials: true });

      // Then add to My Subs
      await axios.post(`${API}/my-subs`, {
        service_id: serviceRes.data.service_id,
        tags: ["From Contacts"],
        notes: `Imported from contacts. Phone: ${contact.phone || 'N/A'}, Email: ${contact.email || 'N/A'}`
      }, { withCredentials: true });

      toast.success(`${contact.name} added to My Subs!`);
      setShowModal(false);
      resetForm();
      
      if (onContactAdded) {
        onContactAdded("mysubs");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add contact");
    } finally {
      setIsAdding(false);
    }
  };

  const resetForm = () => {
    setSelectedContact(null);
    setSelectedCategory("");
    setAddAction(defaultAction);
    setShowManualEntry(false);
    setManualContact({ name: "", phone: "", email: "" });
  };

  const handleClose = () => {
    setShowModal(false);
    resetForm();
  };

  const contact = showManualEntry ? manualContact : selectedContact;

  return (
    <>
      <Button
        onClick={pickContact}
        variant="outline"
        className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E]/10"
        data-testid="add-from-contacts-btn"
      >
        <Contact className="w-4 h-4 mr-2" />
        Add from Contacts
      </Button>

      <Dialog open={showModal} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Contact className="w-5 h-5 text-[#1A4D2E]" />
              {showManualEntry ? "Add Contact Manually" : "Add Contact"}
            </DialogTitle>
            <DialogDescription>
              {showManualEntry 
                ? "Enter the contact details below"
                : "Choose how to add this contact to your network"
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Contact Info Display or Manual Entry */}
            {showManualEntry ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="contact-name">Name *</Label>
                  <Input
                    id="contact-name"
                    value={manualContact.name}
                    onChange={(e) => setManualContact({ ...manualContact, name: e.target.value })}
                    placeholder="Contact name"
                    className="mt-1.5"
                    data-testid="manual-contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-phone">Phone</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={manualContact.phone}
                    onChange={(e) => setManualContact({ ...manualContact, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="mt-1.5"
                    data-testid="manual-contact-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={manualContact.email}
                    onChange={(e) => setManualContact({ ...manualContact, email: e.target.value })}
                    placeholder="contact@email.com"
                    className="mt-1.5"
                    data-testid="manual-contact-email"
                  />
                </div>
              </div>
            ) : selectedContact && (
              <div className="bg-stone-50 rounded-xl p-4">
                <h4 className="font-semibold text-[#1A1A1A] mb-3">{selectedContact.name}</h4>
                <div className="space-y-2 text-sm">
                  {selectedContact.phone && (
                    <div className="flex items-center gap-2 text-[#4A4A4A]">
                      <Phone className="w-4 h-4 text-[#71717A]" />
                      {selectedContact.phone}
                    </div>
                  )}
                  {selectedContact.email && (
                    <div className="flex items-center gap-2 text-[#4A4A4A]">
                      <Mail className="w-4 h-4 text-[#71717A]" />
                      {selectedContact.email}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Category Selection */}
            <div>
              <Label htmlFor="category">Trade Category *</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="mt-1.5" data-testid="contact-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Selection */}
            {(addAction === "choose" || defaultAction === "choose") && (
              <div className="space-y-3">
                <Label>How would you like to add this contact?</Label>
                
                <button
                  onClick={() => setAddAction("mysubs")}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    addAction === "mysubs" 
                      ? "border-[#1A4D2E] bg-[#1A4D2E]/5" 
                      : "border-stone-200 hover:border-stone-300"
                  }`}
                  data-testid="add-to-mysubs-option"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    addAction === "mysubs" ? "bg-[#1A4D2E] text-white" : "bg-stone-100 text-[#4A4A4A]"
                  }`}>
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-semibold text-[#1A1A1A]">Add to My Subs</p>
                    <p className="text-sm text-[#71717A]">Private list only you can see</p>
                  </div>
                  {addAction === "mysubs" && (
                    <Check className="w-5 h-5 text-[#1A4D2E]" />
                  )}
                </button>

                <button
                  onClick={() => setAddAction("listing")}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                    addAction === "listing" 
                      ? "border-[#1A4D2E] bg-[#1A4D2E]/5" 
                      : "border-stone-200 hover:border-stone-300"
                  }`}
                  data-testid="add-as-listing-option"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    addAction === "listing" ? "bg-[#1A4D2E] text-white" : "bg-stone-100 text-[#4A4A4A]"
                  }`}>
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div className="text-left flex-1">
                    <p className="font-semibold text-[#1A1A1A]">Create Public Listing</p>
                    <p className="text-sm text-[#71717A]">Visible in the directory</p>
                  </div>
                  {addAction === "listing" && (
                    <Check className="w-5 h-5 text-[#1A4D2E]" />
                  )}
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={addAction === "listing" ? handleAddAsListing : handleAddToMySubs}
                disabled={isAdding || !selectedCategory || !contact?.name}
                className="flex-1 bg-[#1A4D2E] hover:bg-[#143D24] text-white"
                data-testid="confirm-add-contact-btn"
              >
                {isAdding ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    {addAction === "listing" ? "Create Listing" : "Add to My Subs"}
                  </>
                )}
              </Button>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
            </div>

            {/* Manual entry toggle */}
            {!showManualEntry && isSupported && (
              <button
                onClick={() => setShowManualEntry(true)}
                className="w-full text-center text-sm text-[#71717A] hover:text-[#1A4D2E]"
              >
                Or enter contact details manually
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactPicker;
